import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { supabase, getAuthHeaders } from './supabaseClient';
import LoginScreen from './components/LoginScreen';
import { takeOverSession } from './components/SessionNotice';
import LandingPage from './components/LandingPage';
import UploadScreen from './components/UploadScreen';
import AuditReport from './components/AuditReport';
import HistoryScreen from './components/HistoryScreen';
import AdminDashboard from './components/AdminDashboard';
// HIDDEN: reconciliation imports — uncomment to restore
// import ReconciliationScreen from './components/ReconciliationScreen';
// import ReconciliationHubScreen from './components/ReconciliationHubScreen';
// import LaborReconciliationScreen from './components/LaborReconciliationScreen';
import AccountSetup from './components/AccountSetup';
import AccountSettings from './components/AccountSettings';
import PricingScreen from './components/PricingScreen';
import SetNewPassword from './components/SetNewPassword';
import { getParam, setParams, getInviteCodeFromPath, clearInvitePath, pushView, getView } from './urlState';

const API_BASE = 'https://tm-audit-platform-production.up.railway.app';

// Top-level, non-transient views that participate in browser-history Back support.
// Transient/auth stages (e.g. 'processing') are intentionally excluded so Back
// never lands on a loading screen.
const NAV_VIEWS = ['upload', 'report', 'history', 'account-settings', 'admin', 'pricing', 'recon-hub', 'reconciliation', 'labor-reconciliation'];

// The open audit is reflected in the URL as ?audit=<id> so a browser refresh
// restores the same report instead of dropping the user back on Home. We use a
// query param + replaceState (rather than a path segment) so it works on static
// hosting without SPA rewrite config, and the browser Back button keeps its
// existing behavior (no extra history entries are created).
const getAuditParam = () => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('audit');
};

const setAuditParam = (auditId) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (auditId) url.searchParams.set('audit', String(auditId));
  else url.searchParams.delete('audit');
  window.history.replaceState(window.history.state, '', url);
};

function App() {
  const [appReady, setAppReady] = useState(false);
  const [session, setSession] = useState(null);
  const [stage, setStage] = useState('upload');
  const [auditResults, setAuditResults] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [userProfile, setUserProfile] = useState(() => {
    // Hydrate from the last-known profile so a returning tab paints immediately
    // instead of waiting on the network profile fetch.
    try {
      const cached = typeof window !== 'undefined' && localStorage.getItem('tm_user_profile');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [error, setError] = useState(null);
  // Session guard (backend/session-guard.js). sessionNotice = { retry, busy, failed }
  // when the account is signed in on another device; signedOutNotice = this
  // device's session was ended by "Sign in here instead" elsewhere.
  const [sessionNotice, setSessionNotice] = useState(null);
  const [signedOutNotice, setSignedOutNotice] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('signup');
  const [subscription, setSubscription] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // True when the user arrived via a Supabase password-recovery link. Detected
  // synchronously from the URL hash so we never flash the app before the
  // PASSWORD_RECOVERY event lands. Gates a Set New Password screen.
  const [recoveryMode, setRecoveryMode] = useState(
    () => typeof window !== 'undefined' && window.location.hash.includes('type=recovery')
  );
  // Beta invitation handling. A branded link is https://audee.io/i/<code>; a
  // legacy link is /?invite=<token>. Detected synchronously so we show a
  // "verifying invitation" screen instead of flashing the public Landing page.
  //
  // CRITICAL: opening a link is now READ-ONLY. On load we call the server's
  // read-only /status endpoint (never redemption), so a refresh, a Mail/Safari
  // preview, or a link scanner can NEVER consume an invite. Redemption is a
  // separate, explicit user action (Continue / Go to Audee) or a safely
  // authorized matching-session continuation. See /api/invite/status + /redeem.
  const [inviteToken] = useState(() => getParam('invite'));
  const [inviteCode] = useState(() => getInviteCodeFromPath());
  const hasInvite = !!(inviteToken || inviteCode);
  // Lifecycle: validating | ready | matching | already_active | session_mismatch
  //          | invalid | error | redeeming | done | none
  const [inviteState, setInviteState] = useState(hasInvite ? 'validating' : 'none');
  const [inviteInfo, setInviteInfo] = useState({ message: null, recipientHint: null });
  const [inviteBusy, setInviteBusy] = useState(false);
  const inviteValidatedRef = useRef(false);
  const currentUserIdRef = useRef(null);

  // ── Browser Back support for top-level views (design-partner request 7/17) ───
  // Reflect the navigable `stage` into browser history so the Back button steps
  // back through in-app views instead of leaving the app on the first press.
  // Consecutive duplicates are collapsed so auth/init churn doesn't spam history,
  // and a change that CAME from Back is not re-pushed (no loop).
  const lastViewRef = useRef(null);
  const fromPopstateRef = useRef(false);
  useEffect(() => {
    if (!NAV_VIEWS.includes(stage)) return;        // ignore transient states (e.g. 'processing')
    if (stage === lastViewRef.current) return;     // no real view change
    if (fromPopstateRef.current) {                 // change came FROM Back — don't re-push
      fromPopstateRef.current = false;
      lastViewRef.current = stage;
      return;
    }
    const firstEntry = lastViewRef.current === null;
    pushView(stage, { replace: firstEntry });      // seed initial view w/o an entry; push thereafter
    lastViewRef.current = stage;
  }, [stage]);
  useEffect(() => {
    const onPop = () => {
      const v = getView();
      fromPopstateRef.current = true;
      setStage(NAV_VIEWS.includes(v) ? v : 'upload');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Open every view at the top. Stages swap components without a page
  // navigation, so window.scrollY carries over from the previous view. It only
  // showed up reopening a report from the Recent-audits list — you had to
  // scroll down to reach the list, and report→report has no short interstitial
  // to clamp scroll (the new-audit path is saved only because the viewport-tall
  // processing screen shrinks the document and forces scroll back to 0). This is
  // the general case, so reset on any stage change rather than patching one path.
  // Not scroll restoration — we always go to the top, never remember a position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stage]);

  useEffect(() => {
    // Safety fallback: if onAuthStateChange doesn't fire INITIAL_SESSION
    // quickly, show the app anyway rather than holding a blank screen.
    const fallbackTimer = setTimeout(() => {
      console.warn('[init] onAuthStateChange did not fire INITIAL_SESSION in 1.2s, showing app anyway');
      setAppReady(true);
    }, 1200);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[auth]', event, 'session:', session ? 'yes' : 'no');
      clearTimeout(fallbackTimer);

      // Password recovery: the link sets a temporary session. Show the Set New
      // Password screen instead of dropping into Audee. We keep the session
      // (updateUser needs it) but short-circuit the normal profile/sync flow.
      if (event === 'PASSWORD_RECOVERY') {
        setSession(session);
        setRecoveryMode(true);
        setAppReady(true);
        return;
      }

      const incomingUserId = session?.user?.id || null;
      if (incomingUserId && incomingUserId === currentUserIdRef.current) {
        console.log('[auth] skipping duplicate sync for same user');
        // Same user re-emitting (TOKEN_REFRESHED / USER_UPDATED): the heavy
        // profile / subscription / role sync already ran and needn't repeat, but
        // the event carries a freshly-minted access_token. Keep it in state —
        // otherwise the in-memory token goes stale after the ~1h JWT expiry and
        // every authenticated fetch 401s ("Invalid token"). The internal admin
        // area surfaced this first because Growth is its default tab and shows
        // the failure; the other admin fetches swallow the same 401 silently.
        if (session) setSession(session);
        return;
      }

      if (session) {
        // Render immediately using in-state (cached) profile. Previously first
        // paint waited on a profile query plus two sequential Railway fetches
        // (subscription + role sync); on a stale overnight tab those cold calls
        // stacked on top of the token refresh and made the app take seconds to
        // appear. Everything below the first paint now hydrates in the
        // background and never gates readiness.
        setSession(session);
        currentUserIdRef.current = session.user.id;
        setAppReady(true);
        console.log("App ready - session: yes (hydrating profile in background)");

        (async () => {
          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('*, accounts(*)')
              .eq('user_id', session.user.id)
              .single();
            if (profile) {
              localStorage.setItem('tm_user_profile', JSON.stringify(profile));
              setUserProfile(profile);
            }
          } catch (e) { console.warn('[auth] profile fetch error:', e); }

          try {
            const subRes = await fetch(
              'https://tm-audit-platform-production.up.railway.app/api/stripe/subscription-status',
              { headers: await getAuthHeaders() }
            );
            setSubscription(await subRes.json());
          } catch (e) { console.warn('[auth] subscription fetch error:', e); }

          // Reconcile any pending admin invite for this email → role, and learn
          // whether this user may access the admin area. Canonical source is
          // user_profiles.internal_role on the backend (no more hardcoded emails).
          try {
            const syncRes = await fetch(
              'https://tm-audit-platform-production.up.railway.app/api/auth/sync',
              { method: 'POST', headers: await getAuthHeaders() }
            );
            const syncData = await syncRes.json();
            setIsAdmin(syncData?.is_admin === true);
          } catch (e) { console.warn('[auth] role sync error:', e); }
        })();
      } else {
        localStorage.removeItem('tm_user_profile');
        setUserProfile(null);
        setSubscription(null);
        setIsAdmin(false);
        setSession(null);
        currentUserIdRef.current = null;
        console.log("App ready - session: no");
        setAppReady(true);
      }
    });

    return () => {
      clearTimeout(fallbackTimer);
      subscription?.unsubscribe?.();
    };
  }, []);

  // Proactively refresh the session when a backgrounded tab becomes visible
  // again. Browsers throttle timers in hidden tabs, so the Supabase client's
  // automatic refresh can miss and the stored JWT expires overnight — the next
  // request then hangs/401s ("Invalid token") until a hard reload. Refreshing on
  // wake mints a fresh token up front; the TOKEN_REFRESHED event handled above
  // keeps it in state. This is the fix for the "reopen a day-old tab and it
  // takes forever / needs Cmd+Shift+R" symptom.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session) await supabase.auth.refreshSession();
      } catch (e) { console.warn('[auth] visibility refresh failed:', e); }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // Attach the current session's bearer token when one exists, so /status and
  // /redeem are session-aware (States C/D/G). Logged-out visitors and scanners
  // simply send no token.
  const inviteHeaders = async (base = {}) => {
    const headers = { ...base };
    try {
      const { data } = await supabase.auth.getSession();
      const tok = data?.session?.access_token;
      if (tok) headers.Authorization = `Bearer ${tok}`;
    } catch (_) { /* logged out */ }
    return headers;
  };

  // Explicit redemption — the ONLY state-changing step. Triggered by a user
  // action (Continue / Go to Audee) or a matching session; never on page load.
  const doRedeem = async () => {
    setInviteBusy(true);
    setInviteState('redeeming');
    try {
      const payload = inviteCode ? { code: inviteCode } : { token: inviteToken };
      const res = await fetch(`${API_BASE}/api/invite/redeem`, {
        method: 'POST',
        headers: await inviteHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setInviteInfo(info => ({ ...info, message: data.error || 'This invitation could not be verified.' }));
        setInviteState(data.code === 'SESSION_EMAIL_MISMATCH' ? 'session_mismatch' : 'invalid');
        setInviteBusy(false);
        return;
      }
      // Matching session: already authenticated — nothing to sign in, go in.
      if (data.matching_session || !data.token_hash) {
        setParams({ invite: null }); clearInvitePath();
        setInviteState('done'); setInviteBusy(false);
        return;
      }
      // Logged-out invited user (new, existing, or already-active re-entry):
      // establish the session in-page. SIGNED_IN then lands them on home/upload.
      const { error } = await supabase.auth.verifyOtp({ token_hash: data.token_hash, type: 'magiclink' });
      if (error) {
        setInviteInfo(info => ({ ...info, message: 'We could not sign you in from this invitation. Please try again.' }));
        setInviteState('error'); setInviteBusy(false);
        return;
      }
      setParams({ invite: null }); clearInvitePath();
      setInviteState('done'); setInviteBusy(false);
    } catch (e) {
      setInviteInfo(info => ({ ...info, message: 'This invitation could not be verified. Please try again shortly.' }));
      setInviteState('error'); setInviteBusy(false);
    }
  };

  // READ-ONLY validation. Asks the server what state this invite+session is in,
  // WITHOUT consuming it. Safe to run on load, on refresh, and after sign-out.
  const runInviteValidation = async () => {
    setInviteState('validating');
    try {
      const url = inviteCode
        ? `${API_BASE}/api/invite/${encodeURIComponent(inviteCode)}/status`
        : `${API_BASE}/api/invite/status?token=${encodeURIComponent(inviteToken)}`;
      const res = await fetch(url, { headers: await inviteHeaders() });
      const data = await res.json().catch(() => ({}));
      setInviteInfo({ message: data.message || null, recipientHint: data.recipient_hint || null });
      const ui = data.ui_state || 'error';
      if (ui === 'matching') {
        // Signed in as the invited user: confirm access + drop straight in.
        doRedeem();
      } else {
        setInviteState(ui); // ready | already_active | session_mismatch | invalid | error
      }
    } catch (e) {
      setInviteState('error');
    }
  };

  // A different account is signed in: sign out, then re-validate as logged-out so
  // the invited user can continue. The invitation was never consumed.
  const signOutAndContinueInvite = async () => {
    setInviteState('validating');
    try { await supabase.auth.signOut(); } catch (_) {}
    await runInviteValidation();
  };

  // Kick off read-only validation once, after auth has resolved (so we know
  // whether a session exists and can be session-aware).
  useEffect(() => {
    if (!hasInvite || !appReady || inviteValidatedRef.current) return;
    inviteValidatedRef.current = true;
    runInviteValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInvite, appReady]);

  // The signed-out notice is for the sign-in that follows it, not later ones.
  useEffect(() => { if (session) setSignedOutNotice(false); }, [session]);

  const handleLogin = async (_session) => {
    // No-op: the onAuthStateChange listener (in useEffect above) handles session
    // and profile state when auth events fire. LoginScreen's auth.signInWithPassword
    // triggers that listener directly, so handleLogin doesn't need to do anything.
    // Kept as a function to satisfy LoginScreen's onLogin prop contract.
  };

  const handleSignOut = async (scope = 'global') => {
    // Clear state immediately — don't wait for signOut() which can
    // hang on the Supabase SDK lock bug. UI updates within one render
    // cycle because the session-truthy render gate flips false.
    localStorage.removeItem('tm_user_profile');
    // Also clear Supabase's own session tokens (sb-* keys). signOut()
    // would normally do this, but since it hangs we clear them ourselves
    // — otherwise the next sign-in inherits stale tokens and the auth
    // state ends up inconsistent (profile fetch silently returns null).
    Object.keys(localStorage)
      .filter(k => k.startsWith('sb-'))
      .forEach(k => localStorage.removeItem(k));
    setSession(null);
    setUserProfile(null);
    setSubscription(null);
    setIsAdmin(false);
    setStage('upload');
    setAuditResults(null);
    setError(null);
    setAuditParam(null);
    setSessionNotice(null);

    // Fire signOut in background. If it succeeds, server session is
    // invalidated. If it hangs (SDK bug), we've already cleared local
    // state so the user sees the unauthenticated view regardless.
    supabase.auth.signOut({ scope }).catch(e => console.warn('[auth] signOut error:', e));
  };

  // This browser's session was ended from another device. Sign out LOCALLY only —
  // a global sign-out would also end the device that just took over.
  const handleSignedOutElsewhere = () => {
    setSignedOutNotice(true);
    setLoginMode('login');
    setShowLogin(true);
    handleSignOut('local');
  };

  const handleTakeOverSession = async () => {
    if (!sessionNotice) return;
    setSessionNotice({ ...sessionNotice, busy: true, failed: false });
    const outcome = await takeOverSession();
    if (outcome === 'ok') {
      const { retry } = sessionNotice;
      setSessionNotice(null);
      if (retry) retry();
    } else if (outcome === 'signed_out') {
      handleSignedOutElsewhere();
    } else {
      setSessionNotice({ ...sessionNotice, busy: false, failed: true });
    }
  };

  const handleAuditSubmit = async (file) => {
    // Check subscription gate
    if (subscription && !subscription.can_audit) {
      setStage('pricing');
      return;
    }
    setStage('processing');
    setError(null);
    setSessionNotice(null);

    const formData = new FormData();
    formData.append('document', file);
    // Sent so that a FAILED audit is attributable to an account. On success the
    // account_id reaches the backend via /api/save-audit — but a failure never
    // calls save-audit, which is why /api/audit needs it directly. Optional: the
    // backend treats it as null when absent (uploads can be pre-login).
    if (userProfile?.account_id) formData.append('account_id', userProfile.account_id);
    setUploadedFileName(file.name);

    try {
      const user = session?.user;
      const response = await fetch('https://tm-audit-platform-production.up.railway.app/api/audit', {
        method: 'POST',
        body: formData,
        // Bearer token lets the session guard identify this browser login.
        headers: await getAuthHeaders(user ? { 'x-user-id': user.id } : {}),
      });

      if (!response.ok) {
        const err = await response.json();
        if (err.code === 'session_conflict') {
          setSessionNotice({ retry: () => handleAuditSubmit(file) });
          setStage('upload');
          return;
        }
        if (err.code === 'session_signed_out') {
          handleSignedOutElsewhere();
          return;
        }
        // Backend access gate: trial ended or trial audit cap reached. Take the
        // server's access fields as-is and show the paywall.
        if (err.code === 'trial_expired' || err.code === 'trial_cap_reached') {
          setSubscription(prev => ({ ...(prev || {}), ...(err.access || {}) }));
          setStage('pricing');
          return;
        }
        throw new Error(err.error || 'Audit failed');
      }

      const results = await response.json();

      // Document type gating
      if (subscription && subscription.plan !== 'free') {
        const docType = results.parsedData?.documentType || '';
        // Cotality is gated with Xactimate: it is in exactly the plans that include
        // Xactimate audits (DEC-012, decision 5). Its parse says documentType 'Symbility'.
        const isXact = /xactimate|symbility|cotality/i.test(docType);
        const isTM = /t&m|servpro|invoice|soc/i.test(docType);
        const plan = subscription.plan;
        const isFullAccess = plan === 'all' || plan === 'beta';

        if (!isFullAccess) {
          if (plan === 'tm' && isXact) {
            setError('Your T&M plan does not include Xactimate audits. Upgrade to Xactimate or All Access to audit Xactimate estimates.');
            setStage('upload');
            return;
          }
          if (plan === 'xactimate' && isTM) {
            setError('Your Xactimate plan does not include T&M audits. Upgrade to All Access to audit T&M invoices.');
            setStage('upload');
            return;
          }
        }
      }

      setAuditResults(results);
      setStage('report');

      try {
        const user = session?.user;
        const jobName = results.parsedData.jobName ||
          file.name.replace(/\.[^.]+$/, '').replace(/\s*Summary of Charges.*$/i, '').replace(/\s*as of.*$/i, '').trim();
        const saveRes = await fetch('https://tm-audit-platform-production.up.railway.app/api/save-audit', {
          method: 'POST',
          headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            user_id: user.id,
            account_id: userProfile?.account_id || null,
            job_name: jobName,
            file_name: file.name,
            findings: results.findings,
            parsedData: results.parsedData,
            // Part 6: forward audit runtime (ms) measured server-side in /api/audit
            // so the Growth Dashboard can track per-audit runtime going forward.
            audit_runtime_ms: results.auditRuntimeMs ?? null,
            // Phase 2 (Audee Provenance): forward the Run Manifest + schema version
            // so they persist into audit_history. Additive + optional — the backend
            // saves fine (and strips them) if absent or if migration 0023 is unapplied.
            manifest: results.manifest ?? null,
            schema_version: results.schemaVersion ?? null,
          }),
        });
        // Make the freshly-saved report directly addressable so a refresh
        // restores it. Best-effort — if the id is absent the report still shows.
        try {
          const saveData = await saveRes.json();
          if (saveData?.id) setAuditParam(saveData.id);
        } catch (_) { /* non-JSON / older backend — leave URL as-is */ }
      } catch (e) {
        console.warn('Failed to save audit history:', e);
      }
    } catch (err) {
      setError(err.message);
      setStage('upload');
    }
  };

  const handleShowHistory = () => setStage('history');
  // HIDDEN: reconciliation — uncomment to restore
  // const handleShowReconciliation = () => setStage('recon-hub');
  const handleShowPricing = () => setStage('pricing');

  const handleSelectPlan = async (planId) => {
    try {
      const user = session?.user;
      const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/stripe/create-checkout', {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ user_id: user.id, email: user.email, plan: planId })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error('Checkout error:', e);
    }
  };

  const handleShowAccountSettings = () => setStage('account-settings');
  const handleShowAdmin = () => setStage('admin');

  const handleAccountComplete = (data) => {
    const profile = data.profile || { accounts: data.account, role: 'admin' };
    setUserProfile(profile);
    localStorage.setItem('tm_user_profile', JSON.stringify(profile));
    setStage('upload');
  };

  const handleReopenAudit = async (auditId) => {
    try {
      const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/audit-history/' + auditId, {
        headers: await getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Audit not found (' + res.status + ')');
      const audit = await res.json();
      setAuditResults({
        findings: JSON.parse(audit.findings_json),
        parsedData: JSON.parse(audit.parsed_data_json),
      });
      setUploadedFileName(audit.file_name || '');
      setStage('report');
      setAuditParam(auditId);
      return true;
    } catch (e) {
      console.warn('Failed to reopen audit:', e);
      // Drop a stale/invalid id from the URL so a refresh doesn't loop on it.
      setAuditParam(null);
      return false;
    }
  };

  // The report's "Is this property a home or a business?" answer (GAP-097). The
  // backend re-runs the rules on the saved parse and rewrites the saved audit; the
  // report then shows the new findings. Needs the saved audit's id, which save-audit
  // put in the URL. Throws so the report can show that the answer did not take.
  const handleSetOccupancy = async (occupancy) => {
    const auditId = getAuditParam();
    if (!auditId) throw new Error('This report has not finished saving yet. Try again in a moment.');
    const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/audit-history/' + auditId + '/occupancy', {
      method: 'POST',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ occupancy }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not update the report. Please try again.');
    setAuditResults(prev => ({ ...prev, findings: data.findings, parsedData: data.parsedData }));
  };

  const handleReset = () => {
    setStage('upload');
    setAuditResults(null);
    setError(null);
    setUploadedFileName('');
    setAuditParam(null);
  };

  // Refresh persistence: on load, if the URL carries ?audit=<id>, reload that
  // audit from history and drop the user straight back onto the report instead
  // of Home. Runs once, after auth + profile are ready (the report only renders
  // for a fully set-up user). A missing/foreign audit falls back to Home.
  const auditRestoredRef = useRef(false);
  useEffect(() => {
    if (!session || !userProfile || auditRestoredRef.current) return;
    const auditId = getAuditParam();
    if (!auditId) return;
    auditRestoredRef.current = true;
    (async () => {
      setStage('processing');
      const ok = await handleReopenAudit(auditId);
      if (!ok) setStage('upload');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, userProfile]);

  // Password-recovery link: show the Set New Password screen before anything
  // else (even during init), so the recovery session never lands in the app.
  if (recoveryMode) {
    return (
      <div className="app" data-theme="light">
        <SetNewPassword onComplete={() => {
          // Password updated. Reload into Audee at a clean URL: the now-valid
          // session re-initializes normally (profile/subscription/admin sync).
          window.location.replace(window.location.origin);
        }} />
      </div>
    );
  }

  // ── Beta invitation screens ────────────────────────────────────────────────
  // Read-only until an explicit action. State-specific messaging (Phase 10/11):
  // a legitimate invited user is NEVER shown a generic "invalid" merely because
  // their account or session already exists. Renders before the login/landing
  // gate so an invited (logged-out) user sees their invite, not the Sign Up
  // funnel. Falls through to the normal app once inviteState is 'done'/'none'.
  if (hasInvite && inviteState !== 'done' && inviteState !== 'none') {
    const hint = inviteInfo.recipientHint;
    const dismiss = () => { setParams({ invite: null }); clearInvitePath(); setInviteState('done'); };
    const Shell = (children) => (
      <div className="app" data-theme="light"><div className="processing-screen"><div className="processing-inner">{children}</div></div></div>
    );

    if (inviteState === 'validating' || inviteState === 'redeeming') {
      return Shell(<>
        <div className="spinner" />
        <h2>{inviteState === 'redeeming' ? 'Setting up your access…' : 'Verifying your invitation…'}</h2>
        <p>Just a moment.</p>
      </>);
    }
    if (inviteState === 'ready') {
      return Shell(<>
        <h2>You’ve been invited to Audee</h2>
        {hint && <p>Invitation for <strong>{hint}</strong>.</p>}
        <p>Click below to enter the private Audee beta.</p>
        <button className="login-btn" disabled={inviteBusy} onClick={doRedeem}>Continue to Audee</button>
      </>);
    }
    if (inviteState === 'already_active') {
      return Shell(<>
        <h2>Your Audee beta access is already active</h2>
        {hint && <p>Invitation for <strong>{hint}</strong>.</p>}
        <button className="login-btn" disabled={inviteBusy} onClick={doRedeem}>Go to Audee</button>
      </>);
    }
    if (inviteState === 'session_mismatch') {
      return Shell(<>
        <h2>You’re signed in with a different account</h2>
        <p>{inviteInfo.message || 'This invitation is connected to a different account. Sign out to continue with the invited account.'}</p>
        <button className="login-btn" disabled={inviteBusy} onClick={signOutAndContinueInvite}>Sign out and continue</button>
        <div style={{ marginTop: 12 }}><button className="history-back-link" onClick={dismiss}>Return to Audee</button></div>
      </>);
    }
    if (inviteState === 'error') {
      return Shell(<>
        <h2>We’re having trouble verifying this invitation</h2>
        <p>{inviteInfo.message || 'Please try again shortly.'}</p>
        <button className="login-btn" onClick={runInviteValidation}>Try again</button>
        <div style={{ marginTop: 12 }}><button className="history-back-link" onClick={dismiss}>Go to Audee</button></div>
      </>);
    }
    // 'invalid' — not found / revoked / expired / already used by another account.
    return Shell(<>
      <h2>Invitation not valid</h2>
      <p>{inviteInfo.message || 'This invitation link could not be found. Ask your Audee contact for a new link.'}</p>
      <button className="login-btn" onClick={dismiss}>Go to Audee</button>
    </>);
  }

  if (!appReady) {
    return (
      <div className="app" data-theme="light">
        <div className="processing-screen">
          <div className="processing-inner">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app" data-theme="light">
        {showLogin
          ? <LoginScreen onLogin={handleLogin} onBack={() => { setShowLogin(false); setSignedOutNotice(false); }} initialMode={loginMode} signedOutNotice={signedOutNotice} />
          : <LandingPage onGetStarted={() => { setLoginMode('signup'); setShowLogin(true); }} onSignIn={() => { setLoginMode('login'); setShowLogin(true); }} />
        }
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="app" data-theme="light">
        <AccountSetup
          session={session}
          onComplete={handleAccountComplete}
          onSignInDifferent={() => { setLoginMode('login'); setShowLogin(true); handleSignOut('local'); }}
        />
      </div>
    );
  }

  // Light theme covers the whole logged-in experience — the customer surfaces
  // (report, upload, processing, history, account, pricing) and now the internal
  // admin area too, so admin reads as the same product rather than a separate
  // dark app. Tokens fall back to the dark :root set when data-theme is absent.
  const LIGHT_STAGES = ['report', 'upload', 'processing', 'history', 'account-settings', 'pricing', 'admin'];

  return (
    <div className="app" data-theme={LIGHT_STAGES.includes(stage) ? 'light' : undefined}>
      <div className="sign-out-bar">
        <span className="sign-out-email">{session.user.email}</span>

        <button className="sign-out-btn secondary-link" onClick={handleShowAccountSettings}>Account</button>
        <button className="sign-out-btn" onClick={() => handleSignOut()}>Sign out</button>
      </div>
      {stage === 'pricing' && (
        <PricingScreen
          onBack={() => setStage('upload')}
          onSelectPlan={handleSelectPlan}
          subscription={subscription}
        />
      )}
      {stage === 'upload' && (
        <>

          <UploadScreen onSubmit={handleAuditSubmit} onHistory={handleShowHistory} onReopen={handleReopenAudit} onAdmin={handleShowAdmin} isAdmin={isAdmin} error={error} sessionNotice={sessionNotice} onTakeOverSession={handleTakeOverSession} subscription={subscription} onShowPricing={handleShowPricing} session={session} />
        </>
      )}
      {stage === 'processing' && (
        <div className="processing-screen">
          <div className="processing-inner">
            <div className="spinner" />
            <h2>Analyzing Document</h2>
            <p>Running audit rules against your document...</p>
          </div>
        </div>
      )}
      {stage === 'history' && (
        <HistoryScreen onBack={handleReset} onReopen={handleReopenAudit} session={session} />
      )}
      {/* HIDDEN: reconciliation stages — uncomment to restore
      {stage === 'recon-hub' && (
        <ReconciliationHubScreen
          onBack={handleReset}
          onSelectExpense={() => setStage('reconciliation')}
          onSelectLabor={() => setStage('labor-reconciliation')}
        />
      )}
      {stage === 'reconciliation' && (
        <ReconciliationScreen onBack={() => setStage('recon-hub')} session={session} />
      )}
      {stage === 'labor-reconciliation' && (
        <LaborReconciliationScreen onBack={() => setStage('recon-hub')} />
      )}
      */}
      {stage === 'admin' && (
        <AdminDashboard onBack={handleReset} session={session} />
      )}
      {stage === 'account-settings' && userProfile && (
        <AccountSettings profile={{...userProfile, email: session?.user?.email}} onBack={handleReset} onShowPricing={handleShowPricing} session={session} />
      )}
      {stage === 'report' && auditResults && (
        <AuditReport results={auditResults} onReset={handleReset} fileName={uploadedFileName} session={session} onSetOccupancy={handleSetOccupancy} />
      )}
    </div>
  );
}

export default App;
